import { User } from '../models/core.models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { AppFeatures } from '../app.features';
import { Injectable } from '@angular/core';
import { NomObjectTypes } from '../constants';
import { TranslateService } from '@ngx-translate/core';

export type PropertyVisibility = 'tools' | 'view-properties' | 'summary';

export interface PropertyViewConfig {
  label?: string;
  path?: string | number;
  propertyUri?: string;
  icon?: IconProp;
  selected?: boolean;
  value?: any;
  visibility?: PropertyVisibility[];
  radioGroup?: string;
  isRadio?: boolean;
  permission?: AppFeatures;
  applicable_feed_types?: NomObjectTypes[];
  isTitle?: boolean;
  summaryOrder?: number;
  summaryValue?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeedSettingsService {
  private allConfigurations: PropertyViewConfig[] = [];
  private configLoaded = false;

  constructor(private translate: TranslateService) {}

  async initializeConfigurations(): Promise<void> {
    const keys = [
      'feed.view-settings.title-time-setting',
      'feed.view-settings.view-details',
      'feed.view-settings.post-content',
      'feed.view-settings.edit-categories',
      'feed.view-settings.edit-sentiment',
      'feed.view-settings.edit-related-articles',
      'feed.view-settings.share',
      'feed.view-settings.debug',
      'feed.view-settings.translate',
      'feed.view-settings.members-count',
      'feed.view-settings.language',
      'feed.view-settings.potential-reach',
      'feed.view-settings.estimated-reach',
      'feed.view-settings.sentiment',
      'feed.view-settings.thumbnail',
      'feed.view-settings.author',
      'feed.view-settings.time-published-(relative)',
      'feed.view-settings.time-published-(absolute)',
      'feed.view-settings.time-created-(relative)',
      'feed.view-settings.time-created-(absolute)',
      'feed.view-settings.global-rank',
      'feed.view-settings.source-country',
      'feed.view-settings.country-rank',
      'feed.view-settings.source-visits-per-day',
      'feed.view-settings.post-completeness-score',
      'feed.view-settings.social-handle',
      'feed.view-settings.likes-count',
      'feed.view-settings.dislikes-count',
      'feed.view-settings.comments-count',
      'feed.view-settings.bookmarks-count',
      'feed.view-settings.shares-count',
      'feed.view-settings.quotes-count',
      'feed.view-settings.views',
      'feed.view-settings.followers-count',
      'feed.view-settings.reactions',
      'feed.view-settings.interactions',
      'feed.view-settings.engagements',
      'feed.view-settings.post-view-settings',
      'feed.view-settings.card-view',
      'feed.view-settings.card-view-threads',
      'feed.view-settings.masonry-layout',
      'feed.view-settings.action-buttons',
      'feed.view-settings.transcriptions',
      'forms.form-option.number-distinct-languages',
      'forms.form-option.number-distinct-sources',
      'forms.form-option.number-distinct-brand-names',
      'forms.form-option.number-distinct-authors',
      'forms.form-option.number-distinct-social-users',
      'forms.form-option.number-distinct-countries',
      'forms.form-option.volume-breakdown-by-source-type',
      'forms.form-option.volume-breakdown-by-media-channel',
      'forms.form-option.volume-breakdown-by-media-channel-type',
      'forms.form-option.volume-breakdown-by-sentiment',
      'forms.form-option.total-volume',
      'forms.form-option.total-ave',
      'forms.form-option.total-potential-reach',
      'forms.form-option.total-estimated-reach',
      'forms.form-option.total-source-daily-visits',
      'forms.form-option.total-source-followers',
      'forms.form-option.total-like-count',
      'forms.form-option.total-dislikes-count',
      'forms.form-option.total-comment-count',
      'forms.form-option.total-share-count',
      'forms.form-option.total-post-views',
      'forms.form-option.total-reactions-count',
      'forms.form-option.total-interactions-count',
      'forms.form-option.total-engagement-count',
      'forms.form-option.total-bookmark-count',
      'forms.form-option.total-quote-count',
      'forms.form-option.total-video-length',
    ];

    const translations = await this.translate.get(keys).toPromise();

    this.allConfigurations = [
      // --- TITLES & LAYOUT SETTINGS ---
      {
        label: translations['feed.view-settings.post-view-settings'],
        isTitle: true,
        visibility: ['tools'],
      },
      {
        label: translations['feed.view-settings.card-view'],
        path: 'default',
        propertyUri: 'view_posts_mode_default',
        icon: ['fas', 'align-justify'],
        selected: true,
        isRadio: true,
        visibility: ['tools'],
        radioGroup: 'view_posts_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.card-view-threads'],
        path: 'threads',
        propertyUri: 'view_posts_mode_threads',
        icon: ['fas', 'comments'],
        selected: false,
        isRadio: true,
        visibility: ['tools'],
        radioGroup: 'view_posts_mode',
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.masonry-layout'],
        path: 'gallery',
        propertyUri: 'view_posts_mode_gallery',
        icon: ['fas', 'grip-horizontal'],
        selected: false,
        visibility: ['tools'],
        isRadio: true,
        radioGroup: 'view_posts_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },

      {
        label: translations['feed.view-settings.title-time-setting'],
        isTitle: true,
        visibility: ['tools'],
      },

      {
        label: translations['feed.view-settings.time-published-(relative)'],
        path: 'post_time_published_relative',
        propertyUri: 'post_time_published',
        icon: ['fas', 'upload'],
        selected: false,
        visibility: ['tools'],
        isRadio: true,
        radioGroup: 'post_time_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },

      {
        label: translations['feed.view-settings.time-published-(absolute)'],
        path: 'post_time_published_absolute',
        propertyUri: 'post_time_published_absolute',
        icon: ['fas', 'upload'],
        selected: true,
        visibility: ['tools'],
        isRadio: true,
        radioGroup: 'post_time_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.time-created-(relative)'],
        path: 'post_time_created_relative',
        propertyUri: 'post_time_created',
        icon: ['fas', 'circle-plus'],
        selected: false,
        visibility: ['tools'],
        isRadio: true,
        radioGroup: 'post_time_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.time-created-(absolute)'],
        path: 'post_time_created_absolute',
        propertyUri: 'post_time_created_absolute',
        icon: ['fas', 'circle-plus'],
        selected: false,
        visibility: ['tools'],
        isRadio: true,
        radioGroup: 'post_time_mode',
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },

      // --- ACTION TOOLS ---
      {
        label: translations['feed.view-settings.action-buttons'],
        isTitle: true,
        visibility: ['tools'],
      },

      {
        label: translations['feed.view-settings.view-details'],
        propertyUri: 'tool-view-details',
        path: 'view-details',
        icon: ['fas', 'eye'],
        selected: true,
        visibility: ['tools'],
        permission: AppFeatures.FEED_SEARCH_RESULTS_VIEW_DETAILS,
      },

      {
        label: translations['feed.view-settings.edit-categories'],
        propertyUri: 'tool-edit-categories',
        path: 'edit-categories',
        icon: ['fas', 'tag'],
        selected: true,
        visibility: ['tools'],
        permission: AppFeatures.FEED_SEARCH_RESULTS_SET_CATEGORIES,
      },
      {
        label: translations['feed.view-settings.edit-sentiment'],
        propertyUri: 'tool-edit-sentiment',
        path: 'edit-sentiment',
        icon: ['far', 'face-smile'],
        selected: true,
        visibility: ['tools'],
        permission: AppFeatures.FEED_SEARCH_RESULTS_EDIT_SENTIMENT,
      },
      {
        label: translations['feed.view-settings.edit-related-articles'],
        propertyUri: 'tool-edit-related',
        path: 'edit-related',
        icon: ['fas', 'chain'],
        selected: true,
        visibility: ['tools'],
        permission:
          AppFeatures.FEED_SEARCH_RESULTS_LINK_RELATED_CONTENT_MANUALLY,
      },
      {
        label: translations['feed.view-settings.share'],
        propertyUri: 'tool-share',
        path: 'share',
        icon: ['fas', 'share'],
        selected: true,
        visibility: ['tools'],

        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.debug'],
        propertyUri: 'tool-debug',
        path: 'debug',
        icon: ['fas', 'bug'],
        selected: true,
        visibility: ['tools'],
        permission: AppFeatures.FEED_SEARCH_RESULTS_DEBUG_POST,
      },
      {
        label: translations['feed.view-settings.transcriptions'],
        propertyUri: 'tool-transcriptions',
        path: 'transcriptions',
        icon: ['fas', 'closed-captioning'],
        selected: true,
        visibility: ['tools'],
      },
      {
        label: translations['feed.view-settings.translate'],
        propertyUri: 'tool-translate',
        path: 'translate',
        icon: ['fas', 'language'],
        selected: true,
        visibility: ['tools'],
        permission: AppFeatures.FEED_SEARCH_RESULTS_QUICK_GOOGLE_TRANSLATE,
      },

      {
        label: translations['feed.view-settings.view-properties'],
        isTitle: true,
        visibility: ['view-properties'],
      },

      {
        label: translations['feed.view-settings.post-content'],
        propertyUri: 'post-content',
        path: 'content',
        icon: ['fas', 'rectangle-list'],
        selected: true,
        visibility: ['view-properties'],
      },
      {
        label: translations['feed.view-settings.sentiment'],
        path: 'sentiment',
        propertyUri: 'post_sentiment_id',
        icon: ['fas', 'smile'],
        selected: true,
        visibility: ['tools'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.post-completeness-score'],
        path: 'completeness_score',
        propertyUri: 'post_completeness_score',
        icon: ['fas', 'percent'],
        selected: false,
        visibility: ['tools'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.thumbnail'],
        path: 'thumbnail',
        propertyUri: 'post_thumb_image',
        icon: ['fas', 'image'],
        selected: true,
        visibility: ['tools'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.author'],
        path: 'author.name',
        propertyUri: 'post_author_name',
        icon: ['fas', 'user'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.social-handle'],
        path: 'author.username',
        propertyUri: 'post_author_username',
        icon: ['fas', 'at'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [NomObjectTypes.SOCIAL_FEED],
      },
      {
        label: translations['feed.view-settings.members-count'],
        path: 'story_children_count',
        propertyUri: 'story_children_count',
        icon: ['fas', 'book'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [NomObjectTypes.STORY_FEED],
      },
      {
        label: translations['feed.view-settings.language'],
        path: 'language.title',
        propertyUri: 'post_language_id',
        icon: ['fas', 'globe'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.potential-reach'],
        path: 'potential_reach',
        propertyUri: 'post_potential_reach',
        icon: ['fab', 'chromecast'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.estimated-reach'],
        path: 'estimated_reach',
        propertyUri: 'post_estimated_reach',
        icon: ['fab', 'chromecast'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.global-rank'],
        path: 'source.grank',
        propertyUri: 'post_source_rank_global',
        icon: ['fas', 'trophy'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.source-country'],
        path: 'country.name',
        propertyUri: 'post_section_country_id',
        icon: ['fas', 'location-dot'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.country-rank'],
        path: 'source.crank',
        propertyUri: 'post_source_rank_country',
        icon: ['fas', 'sort-numeric-up'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.source-visits-per-day'],
        path: 'source.visits',
        propertyUri: 'post_source_visits_per_day',
        icon: ['fas', 'eye'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
      },

      // --- SOCIAL STATS ---
      {
        label: translations['feed.view-settings.followers-count'],
        path: 'social_data_stats.followers',
        propertyUri: 'post_social_followers',
        icon: ['fas', 'user-friends'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.likes-count'],
        path: 'social_data_stats.likes_count',
        propertyUri: 'post_social_likes_count',
        icon: ['fas', 'heart'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.dislikes-count'],
        path: 'social_data_stats.dislikes_count',
        propertyUri: 'post_social_dislikes_count',
        icon: ['fas', 'thumbs-down'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.comments-count'],
        path: 'social_data_stats.comments_count',
        propertyUri: 'post_social_comments_count',
        icon: ['fas', 'comments'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.shares-count'],
        path: 'social_data_stats.shares_count',
        propertyUri: 'post_social_shares_count',
        icon: ['fas', 'share'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.quotes-count'],
        path: 'social_data_stats.quotes_count',
        propertyUri: 'post_social_quotes_count',
        icon: ['fas', 'quote-left'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.bookmarks-count'],
        path: 'social_data_stats.bookmarks_count',
        propertyUri: 'post_social_bookmarks_count',
        icon: ['fas', 'share'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.views'],
        path: 'social_data_stats.views_count',
        propertyUri: 'post_social_views_count',
        icon: ['fas', 'eye'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.reactions'],
        path: 'social_data_stats.reactions_count',
        propertyUri: 'post_social_reactions_count',
        icon: ['fas', 'heart-pulse'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.interactions'],
        path: 'social_data_stats.interactions_count',
        propertyUri: 'post_social_interatcions_count',
        icon: ['fas', 'hand-pointer'],
        selected: false,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },
      {
        label: translations['feed.view-settings.engagements'],
        path: 'social_data_stats.engagements_count',
        propertyUri: 'post_social_engagements_count',
        icon: ['fas', 'hand-sparkles'],
        selected: true,
        visibility: ['view-properties'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
      },

      // --- SUMMARY ONLY AGGREGATIONS ---

      //
      // SUMMARY FORM OPTIONS - DISPLAY METRICS
      //
      {
        label: translations['forms.form-option.total-volume'],
        path: 'post_id',
        icon: ['fas', 'chart-column'],
        visibility: ['summary'],
        summaryOrder: 10,
      },
      {
        label: translations['forms.form-option.total-source-daily-visits'],
        path: 'post_source_visits_per_day',
        icon: ['fas', 'eye'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 20,
      },
      {
        label: translations['forms.form-option.total-source-followers'],
        path: 'post_social_followers',
        icon: ['fas', 'user-friends'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 30,
      },
      {
        label: translations['forms.form-option.total-ave'],
        path: 'post_ave',
        propertyUri: 'post_ave',
        icon: ['fas', 'dollar-sign'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 40,
      },
      {
        label: translations['forms.form-option.total-potential-reach'],
        path: 'post_potential_reach',
        icon: ['fab', 'chromecast'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 50,
      },
      {
        label: translations['forms.form-option.total-estimated-reach'],
        path: 'post_estimated_reach',
        icon: ['fab', 'chromecast'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 55,
      },
      {
        label: translations['forms.form-option.number-distinct-sources'],
        path: 'post_source_id',
        icon: ['fas', 'newspaper'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 60,
      },
      {
        label: translations['forms.form-option.number-distinct-brand-names'],
        path: 'post_source_brand_name',
        propertyUri: 'source_brand_name',
        icon: ['fas', 'briefcase'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 65,
      },
      {
        label: translations['forms.form-option.number-distinct-languages'],
        path: 'post_language_id',
        icon: ['fas', 'globe'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 70,
      },
      {
        label: translations['forms.form-option.number-distinct-countries'],
        path: 'section_country_id',
        icon: ['fas', 'globe'],

        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 80,
      },
      {
        label: translations['forms.form-option.number-distinct-authors'],
        path: 'post_author_name',
        propertyUri: 'post_author_name',
        icon: ['fas', 'user'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 90,
      },
      {
        label: translations['forms.form-option.number-distinct-social-users'],
        path: 'post_social_author_id',
        propertyUri: 'post_social_author_id',
        icon: ['fas', 'at'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [NomObjectTypes.SOCIAL_FEED],
        summaryOrder: 100,
      },
      {
        label: translations['forms.form-option.volume-breakdown-by-sentiment'],
        path: 'post_sentiment_id',
        icon: ['fas', 'smile'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 110,
      },
      {
        label:
          translations['forms.form-option.volume-breakdown-by-source-type'],
        path: 'source_type_id',
        propertyUri: 'source_type_id',
        icon: ['fas', 'chart-pie'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 120,
      },
      {
        label:
          translations['forms.form-option.volume-breakdown-by-media-channel'],
        path: 'source_media_channel_id',
        propertyUri: 'source_media_channel_id',
        icon: ['fas', 'at'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.STORY_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 130,
      },
      {
        label:
          translations[
            'forms.form-option.volume-breakdown-by-media-channel-type'
          ],
        path: 'source_media_channel_type_id',
        propertyUri: 'source_media_channel_type_id',
        icon: ['fas', 'at'],
        selected: false,
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.ONLINE_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.CLIPPING_FEED,
          NomObjectTypes.BROADCAST_FEED,
        ],
        summaryOrder: 140,
      },
      {
        label: translations['forms.form-option.total-like-count'],
        path: 'post_social_likes_count',
        icon: ['fas', 'heart'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 150,
      },
      {
        label: translations['forms.form-option.total-dislikes-count'],
        path: 'post_social_dislikes_count',
        icon: ['fas', 'thumbs-down'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 160,
      },
      {
        label: translations['forms.form-option.total-comment-count'],
        path: 'post_social_comments_count',
        icon: ['fas', 'comments'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 170,
      },
      {
        label: translations['forms.form-option.total-share-count'],
        path: 'post_social_shares_count',
        icon: ['fas', 'share'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 180,
      },
      {
        label: translations['forms.form-option.total-bookmark-count'],
        path: 'post_social_bookmarks_count',
        icon: ['fas', 'bookmark'],
        visibility: ['summary'],
        applicable_feed_types: [NomObjectTypes.SOCIAL_FEED],
        summaryOrder: 190,
      },
      {
        label: translations['forms.form-option.total-quote-count'],
        path: 'post_social_quotes_count',
        icon: ['fas', 'quote-left'],
        visibility: ['summary'],
        applicable_feed_types: [NomObjectTypes.SOCIAL_FEED],
        summaryOrder: 200,
      },
      {
        label: translations['forms.form-option.total-post-views'],
        path: 'post_stats_views',
        icon: ['fas', 'eye'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 210,
      },
      {
        label: translations['forms.form-option.total-video-length'],
        path: 'post_attachment_video_length_seconds',
        icon: ['fas', 'video'],
        visibility: ['summary'],
        summaryOrder: 220,
      },
      {
        label: translations['forms.form-option.total-reactions-count'],
        path: 'post_social_reactions_count',
        icon: ['fas', 'heart-pulse'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 230,
      },
      {
        label: translations['forms.form-option.total-interactions-count'],
        path: 'post_social_interactions_count',
        icon: ['fas', 'hand-pointer'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 240,
      },
      {
        label: translations['forms.form-option.total-engagement-count'],
        path: 'post_social_engagements_count',
        icon: ['fas', 'hand-sparkles'],
        visibility: ['summary'],
        applicable_feed_types: [
          NomObjectTypes.COMBINED_FEED,
          NomObjectTypes.SOCIAL_FEED,
          NomObjectTypes.CLIPPING_FEED,
        ],
        summaryOrder: 250,
      },
    ];

    this.configLoaded = true;
  }

  /**
   * Modified to accept an array of contexts.
   * Default: ['view-properties', 'tools']
   */
  async getFeedConfig(
    objectTypeId: NomObjectTypes,
    user: User,
    contexts: PropertyVisibility[] = ['view-properties', 'tools'],
  ): Promise<PropertyViewConfig[]> {
    if (!this.configLoaded) {
      await this.initializeConfigurations();
    }

    return this.allConfigurations.filter((config) => {
      // Check if config visibility intersects with requested contexts
      const isVisible =
        !config.visibility ||
        config.visibility.some((v) => contexts.includes(v));

      const isTypeAllowed =
        !config.applicable_feed_types ||
        config.applicable_feed_types.includes(objectTypeId);

      const hasPermission =
        !config.permission || user?.feature_components?.[config.permission];

      // Logic check: (Matches context OR is a meta-item like title/sep) AND permitted AND feed-type-correct
      return (isVisible || config.isTitle) && isTypeAllowed && hasPermission;
    });
  }

  /**
   * Retrieves items specifically flagged for the Summary view
   */
  async getSummaryFilterOptions(
    objectTypeIds: NomObjectTypes[], // Changed to array
    user: User,
  ): Promise<any[]> {
    if (!this.configLoaded) {
      await this.initializeConfigurations();
    }

    return this.allConfigurations
      .filter((c) => {
        const isSummary = c.visibility?.includes('summary');

        // Check if the config is applicable to EVERY objectTypeId in the input array
        const isTypeAllowed =
          !c.applicable_feed_types ||
          objectTypeIds.every((id) => c.applicable_feed_types.includes(id));

        const hasPermission =
          !c.permission || user?.feature_components?.[c.permission];

        return isSummary && isTypeAllowed && hasPermission;
      })
      .sort((a, b) => (a.summaryOrder ?? 999) - (b.summaryOrder ?? 999))
      .map((c) => ({
        value: c.summaryValue ?? c.propertyUri ?? String(c.path),
        name: c.label,
        icon: c.icon,
      }));
  }

  /**
   * Maps existing selections to the configuration objects
   */
  buildViewConfigFromSelectedPaths(
    feedConfig: PropertyViewConfig[],
    selectedPaths: Array<string | number>,
  ): PropertyViewConfig[] {
    const selectedSet = new Set<string | number>(selectedPaths);

    return feedConfig
      .filter((c) => !c.isTitle)
      .map((c) => ({
        ...c,
        selected: selectedSet.has(c.path!),
        value: c.path,
      }));
  }
}
